import { defineStore } from 'pinia'
import type { SortingState } from '@tanstack/vue-table';


/**
 * Define the shape of your persistent data.
 * This interface is flexible! You can add any new members here, and
 * the persistence logic will handle them automatically.
 */
export interface PersistentStoreState {
  userPreferences: Map<string, string | boolean | number| any>
  recentSearches: string[]
  lastLoginTimestamp: number | null
  isWelcomeMessageDismissed: boolean,
}

// A unique key to identify our store in localStorage
const STORAGE_KEY = 'my-app-persistent-store';

/**
 * Gets the initial state.
 * It tries to load from localStorage and falls back to defaults.
 * This function is exported for use in the persistence plugin.
 */
export const getInitialState = (): PersistentStoreState => {
  // 1. Define the default state for a new user
  const defaultState: PersistentStoreState = {
    userPreferences: new Map<string, string | boolean | number| any>([
      ['theme', 'light'],
      ['notifications', true]
    ]),
    recentSearches: [],
    lastLoginTimestamp: null,
    isWelcomeMessageDismissed: false,
  }

  // 2. Check if we are on the client-side before accessing localStorage
  if (typeof window === 'undefined') {
    return defaultState;
  }

  // 3. Try to retrieve the stored state from localStorage
  const storedStateRaw = localStorage.getItem(STORAGE_KEY);
  if (!storedStateRaw) {
    return defaultState;
  }

  try {
    // 4. Parse the stored state
    const storedState = JSON.parse(storedStateRaw);

    // 5. **Crucially, rehydrate the Map.** JSON.stringify turns Maps into arrays of [key, value] pairs.
    // We need to convert it back into a Map object.
    if (storedState.userPreferences && Array.isArray(storedState.userPreferences)) {
      storedState.userPreferences = new Map<string, string | boolean | number| any>(storedState.userPreferences);
    }

    // 6. Merge the stored state with the default state.
    // This ensures that if you add new properties to the interface in a future
    // update, the app won't crash for users with old localStorage data.
    return { ...defaultState, ...storedState };

  } catch (error) {
    console.error('Error parsing persistent state from localStorage:', error);
    return defaultState; // Fallback to defaults if parsing fails
  }
};


export const usePersistentStore = defineStore('persistent', {
  // The state is initialized by our function
  state: getInitialState,

  // Example getters
  getters: {
    getTheme: (state): string => (state.userPreferences.get('theme') as string) || 'light',
    isUserLoggedIn: (state): boolean => !!state.lastLoginTimestamp,
  },

  // Example actions to mutate the state
  actions: {
    setPreference(key: string, value: string | boolean | number) {
      this.userPreferences.set(key, value);
    },
    
    addRecentSearch(term: string) {
      if (!term || this.recentSearches.includes(term)) return;
      // Add to the beginning and limit the list to 10 items
      this.recentSearches.unshift(term);
      this.recentSearches = this.recentSearches.slice(0, 10);
    },
    
    login() {
      this.lastLoginTimestamp = Date.now();
    },

    logout() {
      this.lastLoginTimestamp = null;
    },

    dismissWelcomeMessage() {
      this.isWelcomeMessageDismissed = true;
    },
  },
})

// Export the key for the plugin to use
export { STORAGE_KEY };
